import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Modal,Dropdown, InputGroup } from 'react-bootstrap';
import { DatePicker } from "../datePicker";
import { displayToastMsg } from '../toastMessages/toastmessages';
import axios from 'axios';
import { useAppSelector } from '../../redux/hooks';
import { Logger } from '../../../../share/utils/logger';

interface EditApptModalProps {
    showEditModal: boolean;
    setShowEditModal: (show: boolean) => void;
    appointmentToEdit: any;
    handleSaveAppointment: (updatedAppointment: any) => void;
    handleDeleteAppointment: () => void;
    handleRemoveAppointment:(appointmentToRemove: any) => void;
    showDeleteAppModal: boolean;
    setShowDeleteAppModal: (show: boolean) => void;
}

const EditApptModal: React.FC<EditApptModalProps> = ({
    showEditModal,
    setShowEditModal,
    appointmentToEdit,
    handleSaveAppointment,
    handleRemoveAppointment,
    handleDeleteAppointment,
    showDeleteAppModal,
    setShowDeleteAppModal
}) => {
    const [apptTitle, setApptTitle] = useState<string>("");
    const [apptDetails, setApptDetails] = useState<string>("");
    const [apptDate, setApptDate] = useState<string>("");
    const [apptTime, setApptTime] = useState<string>("");
    const [hour, setHour] = useState<string>("");
    const [minute, setMinute] = useState<string>("");
    const [amPm, setAmPm] = useState<string>("AM");
    const [isPrivate, setIsPrivate] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [firmFiles, setFirmFiles] = useState<any[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<any>( null ); // better give file a type
    const [hasExistingFileLink, setHasExistingFileLink] = useState<boolean>(false);
    const [fileMatterNumber, setFileMatterNumber] = useState<string>("");

    const SESSION_INFO = useAppSelector((state) => state.SessionInfo);

    const fetchFirmFiles = async () => {
        if (!SESSION_INFO?.firm?.id) {
            return;
        }
        setIsLoadingFiles(true);
        try {
            const queryParams = new URLSearchParams({
                LawyerID: SESSION_INFO.firm.id.toString(),
                SessionID: SESSION_INFO.sid
            }).toString();
            const response = await axios.get(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/getfilebylawyer?${queryParams}`);
            setFirmFiles(response.data.result);
        } catch (error) {
            Logger.error('Error fetching firm files:', error);
        } finally {
            setIsLoadingFiles(false);
        }
    };

    const filtteredFiles = firmFiles.filter((file: any) => {
        const searchText = searchTerm.toLowerCase();
        return file.MatterNumber
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ?? false;
    });

    const handleFileSelect = (file: any) => {
        setSelectedFile(file);
        setSearchTerm(file.MatterNumber);
        setShowDropdown(false);
        setFileMatterNumber(file.MatterNumber);
    };

    const parseTimeString = (timeStr: string) => {
        if (!timeStr) {
            setHour("");
            setMinute("");
            setAmPm("AM");
            return;
        }

        const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
            setHour(timeMatch[1]);
            setMinute(timeMatch[2]);
            setAmPm(timeMatch[3].toUpperCase());
        } else {
            setHour("");
            setMinute("");
            setAmPm("AM");
        }
    };

    const formatTimeString = () => {
        if (!hour || !minute) return "";
        return `${hour}:${minute.padStart(2, '0')} ${amPm}`;
    };

    // Extract title without time from shortDescr
    const extractTitleFromShortDescr = (shortDescr: string): string => {
        if (!shortDescr) return "";
        
        // Match pattern: "Title - time AM/PM" or "Title - time:minute AM/PM"
        const timePattern = /\s*-\s*\d{1,2}:\d{1,2}\s*(AM|PM)\s*$/i;
        return shortDescr.replace(timePattern, "").trim();
    };

    
    const buildNewShortDescr = (title: string, time: string): string => {
        if (!title || !time) return title;
        return `${title} - ${time}`;
    };

    
    const buildNewLongDescr = (title: string, time: string): string => {
        if (!title || !time) return title;
        return `${time} - ${title}`;
    };

    // Add this helper function to extract title from longDescr
    const extractTitleFromLongDescr = (longDescr: string): string => {
        if (!longDescr) return "";
        
        // Match pattern: "time AM/PM - Title"
        const timePattern = /^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*/i;
        return longDescr.replace(timePattern, "").trim();
    };

    useEffect(() => {
        if (appointmentToEdit && showEditModal) {
            // Extract title without time for display
            const titleWithoutTime = extractTitleFromShortDescr(appointmentToEdit.shortDescr || appointmentToEdit.title);
            setApptTitle(titleWithoutTime);
            setApptDetails(appointmentToEdit.details || appointmentToEdit.psm || "")
            setApptDate(appointmentToEdit.calendarItemDate || "");
            setApptTime(appointmentToEdit.calendarItemTime || "");
            parseTimeString(appointmentToEdit.calendarItemTime || "");
            setValidationError(null);
            const xmlString = appointmentToEdit.xmldata || "";
            const linkToFileMatch = xmlString.match(/LinkToFile="([^"]*)"/);
            const hasLinkToFile = linkToFileMatch && linkToFileMatch[1] && linkToFileMatch[1].trim() !== "";
            //dont use appId to check if there is a file link, use the xml data to check if there is a file link, since appId may not in task system table
            //const hasFile = !!appointmentToEdit.appId;
            setHasExistingFileLink(hasLinkToFile);
            if (!hasLinkToFile) {
                setSelectedFile(null);
                setSearchTerm("");
            }
        }
    }, [appointmentToEdit, showEditModal]);

    useEffect(() => {
        setApptTime(formatTimeString());
    }, [hour, minute, amPm]);

    const handleSave = async () => {
        if (!apptTitle.trim()) {
            setValidationError("Appointment title is required");
            return;
        }

        if (!apptDate) {
            setValidationError("Appointment date is required");
            return;
        }
        // Build new descriptions with updated time
        const newTime = formatTimeString();
        const newShortDescr = buildNewShortDescr(apptTitle.trim(), newTime);
        const newLongDescr = buildNewLongDescr(apptTitle.trim(), newTime);
        
        let updatedXmldata: string;
        let linkID: string;
        let tableLinkedToID: string;
        let recordID: number;
        
        if (hasExistingFileLink) { 
            linkID = 'AppID';
            tableLinkedToID = 'TPAppXML';
            recordID = null;
            const xmlString = appointmentToEdit.xmldata || "";
            const linkToFileMatch = xmlString.match(/LinkToFile="([^"]*)"/);
            if (linkToFileMatch && linkToFileMatch[1]) {
                try {
                    recordID = Number(linkToFileMatch[1]);
                } catch (error) {
                    Logger.error('Error converting LinkToFile to number:', error);
                    recordID = null;
                }
            } else {
                // this is safety that if the xmldata is not vaild but the file is catched.
                recordID = appointmentToEdit.appId;
            }
        } else {
            linkID = selectedFile?"AppID" : null;
            tableLinkedToID = selectedFile?"TPAppXML" : null;
            recordID = selectedFile?.appId ?? null;

        }
        try {
            const xmlString = appointmentToEdit.xmldata || "<calendar/>";
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
            const parseError = xmlDoc.querySelector('parsererror');
            if (parseError) {
                throw new Error('XML parsing error');
            }

            let calendarElement: Element | null = null;
            if (xmlDoc.documentElement && xmlDoc.documentElement.nodeName === 'calendar') {
                calendarElement = xmlDoc.documentElement;
            } else {
                calendarElement = xmlDoc.querySelector('calendar');
            }
            if (!calendarElement) {
                calendarElement = xmlDoc.createElement('calendar');
            }
            calendarElement.setAttribute('calendarItemTimeMin', minute.padStart(2, '0'));
            calendarElement.setAttribute('calendarItemTimeAmPm', amPm.toUpperCase());
            calendarElement.setAttribute('shortDescrtmp', apptTitle.trim());
            calendarElement.setAttribute('calendarItemTimeHour', hour.padStart(2, '0'));
            calendarElement.setAttribute('LinkToFile', recordID.toString());
            const serializer = new XMLSerializer();
            updatedXmldata = serializer.serializeToString(calendarElement);
        } catch (error) {
            console.error('Error processing XML:', error instanceof Error ? error : (error?.message || error));
            // Fallback to minimal valid XML with proper escaping
            const escapeXmlAttr = (str: string) => {
                return str
                    .replace(/&/g, '&amp;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');
            };
            updatedXmldata = `<calendar calendarItemTimeMin="${minute.padStart(2, '0')}" calendarItemTimeAmPm="${amPm.toUpperCase()}" shortDescrtmp="${escapeXmlAttr(apptTitle.trim())}" calendarItemTimeHour="${hour.padStart(2, '0')}"/>`;
        }
        const updatedAppointment = {
            ...appointmentToEdit,
            shortDescr: newShortDescr,
            longDescr: newLongDescr,
            details: apptDetails,
            calendarItemDate: apptDate,
            calendarItemTime: newTime,
            privateItem: isPrivate ? "1" : "0",
            xmldata: updatedXmldata,
            linkID: linkID,
            tableLinkedToID: tableLinkedToID,
            recordID: recordID
        };

        try {
            await handleSaveAppointment(updatedAppointment);
            
            displayToastMsg({
                id: Date.now(),
                type: "success",
                msg: "Event updated."
            }, null);
        } catch (error) {
            console.error('Error saving appointment:', error);
            displayToastMsg({
                id: Date.now(),
                type: "error",
                msg: "Failed to update appointment"
            }, null);
        }
    };
    const handleRemoveAppWithToast = (appointmentToEdit) => {
            handleRemoveAppointment(appointmentToEdit);
            // Display a success toast for deletion
            displayToastMsg({
                id: Date.now(),
                type: "success",
                msg: "Event updated."
            }, null);
        };
    return (
        <>
        {/* Edit Appointment Modal */}
        <Modal size="sm" className="modal-sm-long" centered show={showEditModal} onHide={() => {
            setValidationError(null);
            setShowEditModal(false);
        }}>
            <Modal.Header closeButton={false}>
                <h2>Edit Appointment</h2>
            </Modal.Header>
            <Modal.Body>
                {validationError && (
                    <Alert variant="danger" className="mb-3">
                        {validationError}
                    </Alert>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Appointment Title</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={apptTitle}
                            onChange={(e) => setApptTitle(e.target.value)}
                            placeholder={apptTitle || "Enter appointment title"}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Details</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3}
                            value={apptDetails}
                            onChange={(e) => setApptDetails(e.target.value)}
                            placeholder={apptDetails || "Enter appointment details"}
                        />
                    </Form.Group>
                    {!hasExistingFileLink && (
                        <Form.Group className="mb-3 position-relative">
                        <Form.Label>Link to File</Form.Label>
                        <InputGroup>
                            <InputGroup.Text className="bg-transparent border-end-0">
                                <i className="fa-duotone fa-magnifying-glass"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search file # "
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                    setSelectedFile(null); 
                                }}
                                onFocus={() => {
                                    setShowDropdown(true);
                                    fetchFirmFiles();
                                }}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                className="border-start-0 shadow-none"
                            />
                        </InputGroup>
                            {showDropdown && (
                                <div className="dropdown-menu show w-100 p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {isLoadingFiles ? (
                                        <div className="dropdown-item-text text-muted text-center py-2">Loading...</div>
                                    ) : (
                                        <>
                                            {filtteredFiles.length > 0 ? (
                                                filtteredFiles.map((file: any) => (
                                                    <button
                                                        key={file.appId}
                                                        type="button"
                                                        className="dropdown-item py-2"
                                                        onClick={() => handleFileSelect(file)}
                                                    >
                                                        <div className="fw-bold">{file.MatterNumber}</div>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="dropdown-item-text text-muted text-center py-2">No files found</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </Form.Group>
                        )}
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                            {/* Date Picker */}
                            <div style={{ flex: 1 }}>
                                <Form.Label>Date</Form.Label>
                                <DatePicker
                                    date={apptDate}
                                    setDate={(newDate) => setApptDate(newDate)}
                                    placeholder={apptDate || "Select date"}
                                    popperPlacement="top-start"
                                    portalId="root"
                                />
                            </div>
                            {/* Time picker */}
                            <div style={{ flex: 1 }}>
                                <Form.Label>Time</Form.Label>
                                <div className="d-flex gap-2 align-items-center">
                                    <Form.Control 
                                        type="number" 
                                        min="1" 
                                        max="12"
                                        value={hour}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                                                setHour(value);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const value = parseInt(e.target.value);
                                            if (value < 1) setHour('1');
                                            if (value > 12) setHour('12');
                                        }}
                                        placeholder="Hour"
                                        style={{ flex: 1, minWidth: 80 }}
                                    />
                                    <span>:</span>
                                    <Form.Select
                                        value={minute}
                                        onChange={(e) => setMinute(e.target.value)}
                                        style={{ flex: 1, minWidth: 80 }}
                                        >
                                        <option value="00">00</option>
                                        <option value="15">15</option>
                                        <option value="30">30</option>
                                        <option value="45">45</option>
                                        </Form.Select>
                                    <Form.Select 
                                        value={amPm}
                                        onChange={(e) => setAmPm(e.target.value)}
                                        style={{ flex: 1, minWidth: 80 }}
                                    >
                                        <option value="AM">AM</option>
                                        <option value="PM">PM</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>
                    </Form.Group>
                </Form>
            <Button variant="link" className="text-danger p-0 mt-4 ms-4 text-start" onClick={handleDeleteAppointment}>
                    <i className="fa-regular fa-trash-can status-danger"></i>Delete Appointment
            </Button>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button variant="text" onClick={() => {
                    setValidationError(null);
                    setShowEditModal(false);
                }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
        {/* Delete Appointment Confirmation Modal */}
        <Modal centered size="sm" show={showDeleteAppModal} onHide={() => setShowDeleteAppModal(false)}>
            <Modal.Header closeButton>
                <h2>Delete Appointment</h2>
            </Modal.Header>
            <Modal.Body>
                <div className="alert alert-danger">
                    Are you sure you want to remove this appointment?
                </div>
                <p>The appointment and its details will be permanently removed. This action cannot be undone.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={() => setShowDeleteAppModal(false)}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={() => handleRemoveAppWithToast(appointmentToEdit)}>
                    Delete
                </Button>
            </Modal.Footer>
        </Modal>
        
    </>
    );
};

export default EditApptModal;
